import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Popover, { PopoverBody } from '@lib/components/Popover/Popover';
import Typography from '@lib/components/Typography/Typography';
import CitationChip from './CitationChip';
import { groupPageRanges, PageRange } from './utils/citationPages';
import styles from './AiAssistant.module.scss';

// Ranges shown inline after the document chip; the rest fold into "+N pages",
// counting the pages (not the ranges) they hold.
const VISIBLE_PAGE_RANGES = 3;

type Props = {
  // Verified pages (ascending) of one document chip.
  pages: number[];
  onOpenPage: (page: number) => void;
};

function PageChip({
  range,
  onOpenPage,
}: {
  range: PageRange;
  onOpenPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <CitationChip
      label={
        range.from === range.to
          ? t('ai-citation-page', { page: range.from })
          : t('ai-citation-page-range', { from: range.from, to: range.to })
      }
      ariaLabel={t('ai-citation-open-page', { page: range.from })}
      onClick={() => onOpenPage(range.from)}
    />
  );
}

// Verified page links rendered after an inline document chip: one
// chip per consecutive run of pages ("p. 3", "pp. 10–11"), each opening the
// document at the run's first page. The chip itself keeps opening at page 1.
function CitationPageChips({ pages, onOpenPage }: Props) {
  const { t } = useTranslation();
  const { visible, hidden } = useMemo(() => {
    const ranges = groupPageRanges(pages);
    return {
      visible: ranges.slice(0, VISIBLE_PAGE_RANGES),
      hidden: ranges.slice(VISIBLE_PAGE_RANGES),
    };
  }, [pages]);
  const hiddenPageCount = hidden.reduce(
    (count, range) => count + range.to - range.from + 1,
    0,
  );

  const popoverBody = useCallback<PopoverBody>(
    ({ handleClose }) => (
      <div className={styles.citationPagePopover}>
        {hidden.map((range) => (
          <PageChip
            key={range.from}
            range={range}
            onOpenPage={(page) => {
              handleClose();
              onOpenPage(page);
            }}
          />
        ))}
      </div>
    ),
    [hidden, onOpenPage],
  );

  return (
    <>
      {visible.map((range) => (
        <PageChip key={range.from} range={range} onOpenPage={onOpenPage} />
      ))}
      {!!hidden.length && (
        <Popover
          trigger="click"
          placement="bottom"
          width={0}
          body={popoverBody}
        >
          {({ isOpen }) => (
            <button
              type="button"
              className={styles.citationPageChip}
              data-active={isOpen || undefined}
            >
              <Typography variant="caption" component="span">
                {t('ai-citation-more-pages', { count: hiddenPageCount })}
              </Typography>
            </button>
          )}
        </Popover>
      )}
    </>
  );
}

export default CitationPageChips;
